import { Box, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { animated, useSpring } from "@react-spring/web";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuLayoutDashboard } from "react-icons/lu";
import muiSytle from "../../../style/components-style/muiStyle";
import { useColorMode } from "../../../theme/ThemeProviderWrapper";
import BuildingSkyline from "./BuildingSkyline";
import "./GlobalView.css";

const AnimatedBox = animated(Box);

/**
 * GlobalViewContainer — Portfolio view panel.
 *
 * Props:
 *   buildings        — all buildings from dashboard list
 *   selectedBuildings — currently selected buildings
 *   aggregatedData   — pre-fetched GWP data from parent (useBuildingAggregatedData)
 *   isLoading        — loading flag from the parent's TanStack query
 *
 * IMPORTANT: This component does NOT self-fetch. All API calls are owned by
 * the parent (BuildingDashboard) via useBuildingAggregatedData. This prevents
 * duplicate calls to get_global_view_data and avoids firing when panel is closed.
 */
const GlobalViewContainer = ({ buildings = [], selectedBuildings = [], aggregatedData: aggregatedDataProp, isLoading = false }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDark = theme.palette.mode === "dark";

  // Use selected buildings if available, otherwise use all buildings
  const displayBuildings = useMemo(() => (selectedBuildings.length > 0 ? selectedBuildings : buildings), [selectedBuildings, buildings]);

  // Enrich aggregated data with categoryBreakdown (computed locally — no API needed)
  const aggregatedData = useMemo(() => {
    if (!aggregatedDataProp) return null;
    return {
      ...aggregatedDataProp,
      buildingCount: displayBuildings.length,
      categoryBreakdown: displayBuildings.reduce((acc, b) => {
        const cat = b.building_category || "Other";
        acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {}),
    };
  }, [aggregatedDataProp, displayBuildings]);

  // loading = parent's TanStack Query loading flag
  const loading = isLoading;

  // Animation for tab content
  const fadeIn = useSpring({
    opacity: loading ? 0.5 : 1,
    transform: loading ? "translateY(10px)" : "translateY(0px)",
    config: { tension: 200, friction: 20 },
  });

  if (displayBuildings.length === 0) {
    return (
      <Box className="global-view-container" data-theme={mode}>
        {/* Header */}
        <Box className="global-view-header">
          <Box className="global-view-title-section">
            <Typography sx={{ ...muiSytle.typographyPageTitle, color: isDark ? "#f1f5f9" : "black" }}>{t("Portfolio Overview")}</Typography>
            <Typography sx={{ ...muiSytle.typographyPageSubtitle, color: isDark ? "#94a3b8" : "#4b5563" }}>
              {t("Aggregated sustainability insights across")} {displayBuildings.length} {t("buildings")}
            </Typography>
          </Box>

          {/* Quick Stats */}
          <Box className="global-view-quick-stats">
            <Box className="quick-stat-card" style={{ borderColor: "#3b82f640" }}>
              <Box className="quick-stat-icon" style={{ backgroundColor: "#3b82f620", color: "#3b82f6" }}>
                <span className="quick-stat-value">{displayBuildings.length}</span>
              </Box>
              <Typography className="quick-stat-label">{t("Buildings")}</Typography>
            </Box>
            <Box className="quick-stat-card" style={{ borderColor: "#10b98140" }}>
              <Box className="quick-stat-icon" style={{ backgroundColor: "#10b98120", color: "#10b981" }}>
                <span className="quick-stat-value">{loading ? "..." : formatToTons(aggregatedData?.totalGwp || 0)}</span>
                <span className="quick-stat-suffix">t</span>
              </Box>
              <Typography className="quick-stat-label">{t("Total Carbon")}</Typography>
            </Box>
            <Box className="quick-stat-card" style={{ borderColor: "#f59e0b40" }}>
              <Box className="quick-stat-icon" style={{ backgroundColor: "#f59e0b20", color: "#f59e0b" }}>
                <span className="quick-stat-value">{Object.keys(aggregatedData?.categoryBreakdown || {}).length}</span>
              </Box>
              <Typography className="quick-stat-label">{t("Categories")}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Tab Navigation - Only Building Overview */}
        <Box className="global-view-tabs-container">
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} className="global-view-tabs" TabIndicatorProps={{ style: { backgroundColor: "#16a34a" } }}>
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LuLayoutDashboard size={18} />
                  <span>{t("Building Overview")}</span>
                </Box>
              }
              className={`global-view-tab ${activeTab === 0 ? "active" : ""}`}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <AnimatedBox style={fadeIn} className="global-view-content">
          {loading ? (
            <Box sx={{ p: 3 }}>
              {/* Skeleton Loading - Animated Cards */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    height: 24,
                    width: 200,
                    bgcolor: "#e2e8f0",
                    borderRadius: 1,
                    mb: 2,
                    animation: "skeleton-pulse 1.5s ease-in-out infinite",
                  }}
                />
                <Box sx={{ display: "flex", gap: 3 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: 120,
                        bgcolor: "#f1f5f9",
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        animation: `skeleton-pulse 1.5s ease-in-out ${i * 0.1}s infinite`,
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    height: 20,
                    width: 150,
                    bgcolor: "#e2e8f0",
                    borderRadius: 1,
                    mb: 2,
                    animation: "skeleton-pulse 1.5s ease-in-out 0.2s infinite",
                  }}
                />
                <Box sx={{ display: "flex", gap: 2 }}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: 100,
                        bgcolor: "#f1f5f9",
                        borderRadius: 3,
                        border: "1px solid #e2e8f0",
                        animation: `skeleton-pulse 1.5s ease-in-out ${i * 0.15}s infinite`,
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: 3 }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 400,
                    bgcolor: "#f1f5f9",
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    animation: "skeleton-pulse 1.5s ease-in-out 0.3s infinite",
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    height: 400,
                    bgcolor: "#f1f5f9",
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    animation: "skeleton-pulse 1.5s ease-in-out 0.4s infinite",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <BuildingSkyline buildings={displayBuildings} aggregatedData={aggregatedData} loading={loading} />
          )}
        </AnimatedBox>
      </Box>
    );
  }

  // Format number to tons
  const formatToTons = (kg) => {
    if (!kg) return "0";
    const tons = kg / 1000;
    if (tons >= 1000) return (tons / 1000).toFixed(1) + "k";
    if (tons >= 1) return tons.toFixed(1);
    return Math.round(kg);
  };

  return (
    <Box className="global-view-container">
      {/* Header */}
      <Box className="global-view-header">
        <Box className="global-view-title-section">
          <Typography variant="Page Title">{t("Portfolio Overview")}</Typography>
          <Typography variant="Page Subtitle">{t("Aggregated sustainability insights across")} {displayBuildings.length} {t("buildings")}</Typography>
        </Box>

        {/* Quick Stats */}
        <Box className="global-view-quick-stats">
          <Box className="quick-stat-card" style={{ borderColor: "#3b82f640" }}>
            <Box className="quick-stat-icon" style={{ backgroundColor: "#3b82f620", color: "#3b82f6" }}>
              <span className="quick-stat-value">{displayBuildings.length}</span>
            </Box>
            <Typography className="quick-stat-label">{t("Buildings")}</Typography>
          </Box>
          <Box className="quick-stat-card" style={{ borderColor: "#10b98140" }}>
            <Box className="quick-stat-icon" style={{ backgroundColor: "#10b98120", color: "#10b981" }}>
              <span className="quick-stat-value">{loading ? "..." : formatToTons(aggregatedData?.totalGwp || 0)}</span>
              <span className="quick-stat-suffix">t</span>
            </Box>
            <Typography className="quick-stat-label">{t("Total Carbon")}</Typography>
          </Box>
          <Box className="quick-stat-card" style={{ borderColor: "#f59e0b40" }}>
            <Box className="quick-stat-icon" style={{ backgroundColor: "#f59e0b20", color: "#f59e0b" }}>
              <span className="quick-stat-value">{Object.keys(aggregatedData?.categoryBreakdown || {}).length}</span>
            </Box>
            <Typography className="quick-stat-label">{t("Categories")}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Tab Navigation - Only Building Overview */}
      <Box className="global-view-tabs-container">
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} className="global-view-tabs" TabIndicatorProps={{ style: { backgroundColor: "#16a34a" } }}>
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LuLayoutDashboard size={18} />
                <span>{t("Building Overview")}</span>
              </Box>
            }
            className={`global-view-tab ${activeTab === 0 ? "active" : ""}`}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <AnimatedBox style={fadeIn} className="global-view-content">
        {loading ? (
          <Box sx={{ p: 3 }}>
            {/* Skeleton Loading - Animated Cards */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  height: 24,
                  width: 200,
                  bgcolor: "#e2e8f0",
                  borderRadius: 1,
                  mb: 2,
                  animation: "skeleton-pulse 1.5s ease-in-out infinite",
                }}
              />
              <Box sx={{ display: "flex", gap: 3 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      height: 120,
                      bgcolor: "#f1f5f9",
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      animation: `skeleton-pulse 1.5s ease-in-out ${i * 0.1}s infinite`,
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  height: 20,
                  width: 150,
                  bgcolor: "#e2e8f0",
                  borderRadius: 1,
                  mb: 2,
                  animation: "skeleton-pulse 1.5s ease-in-out 0.2s infinite",
                }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      flex: 1,
                      height: 100,
                      bgcolor: "#f1f5f9",
                      borderRadius: 3,
                      border: "1px solid #e2e8f0",
                      animation: `skeleton-pulse 1.5s ease-in-out ${i * 0.15}s infinite`,
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 3 }}>
              <Box
                sx={{
                  flex: 1,
                  height: 400,
                  bgcolor: "#f1f5f9",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  animation: "skeleton-pulse 1.5s ease-in-out 0.3s infinite",
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  height: 400,
                  bgcolor: "#f1f5f9",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  animation: "skeleton-pulse 1.5s ease-in-out 0.4s infinite",
                }}
              />
            </Box>
          </Box>
        ) : (
          <BuildingSkyline buildings={displayBuildings} aggregatedData={aggregatedData} loading={loading} />
        )}
      </AnimatedBox>
    </Box>
  );
};

export default GlobalViewContainer;
